import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '../api/Api'

/**
 * Inbox-style POS notifications backed by the `pos_notifications` table.
 *
 * The store keeps three pieces of canonical state:
 *  - `items`        — the rows fetched so far, newest-first.
 *  - `unreadCount`  — the server-authoritative unread count for the bell badge.
 *  - `bubbleQueue`  — sticky in-app bubbles bottom-right; pushed during
 *                     `poll()` for every fresh unread row, drained only by
 *                     `dismissBubble()` / `clearBubbles()` / `reset()` (no
 *                     timer). The container component renders the queue.
 *
 * The polling loop calls /poll every 30s (configurable) with the cached
 * `lastId` so the server only returns rows we haven't seen yet. New items
 * are prepended onto `items` AND queued onto `bubbleQueue` so the user
 * sees a persistent heads-up until they click the bubble.
 */

export type NotificationSeverity = 'info' | 'success' | 'warning' | 'urgent'

export interface PosNotification {
  id: number
  uid: string
  record_type: string | null
  record_id: number | null
  title: string
  body: string | null
  category: string | null
  severity: NotificationSeverity | string
  link: string | null
  meta: Record<string, any>
  from_users_id: number | null
  read_at: string | null
  dismissed_at: string | null
  created_at: string | null
}

interface ListResponseData {
  items: PosNotification[]
  unread_count: number
  next_cursor: number | null
}

interface PollResponseData {
  items: PosNotification[]
  unread_count: number
  last_id: number
}

const BASE = '/clients/system/notifications/'

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<PosNotification[]>([])
  const unreadCount = ref(0)
  const lastId = ref(0)
  const initialLoaded = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  /** Pub/sub hop for "open this sales order in a modal" intent. Notifications
   *  with `link = "sales-order:UID"` set this ref via {@see openSalesOrder};
   *  the POS Screen (POSListView) watches it, fetches the order, mounts a
   *  detail modal, then clears the ref via {@see consumePendingSalesOrder}.
   *  Screens that don't watch this ref simply ignore the signal, so the
   *  pattern degrades gracefully off the POS surface. */
  const pendingSalesOrderUid = ref<string | null>(null)

  /**
   * Sticky in-app notification bubbles, bottom-right. Every fresh unread
   * row picked up by {@see poll} is pushed here so the cashier sees a
   * persistent bubble (no auto-dismiss timer) until they click it open or
   * hit the explicit X button. The transient Toast.show() path is gone —
   * the bubble IS the heads-up surface now.
   */
  const bubbleQueue = ref<PosNotification[]>([])

  // Polling timer handle. Kept module-level inside the store so the
  // start/stop calls are idempotent across re-mounts of the header bell.
  let pollTimer: ReturnType<typeof setInterval> | null = null

  const unread = computed(() => items.value.filter((n) => !n.read_at && !n.dismissed_at))
  const recent = computed(() => items.value.slice(0, 10))

  /**
   * Initial fetch — usually called once after login. Pulls the latest 30
   * rows and seeds `lastId` so the polling loop starts from there.
   */
  async function load(opts: { unreadOnly?: boolean; limit?: number } = {}) {
    loading.value = true
    error.value = null
    try {
      const res = await api.post<ListResponseData>(
        BASE + 'list',
        {
          unread_only: opts.unreadOnly ? 1 : 0,
          limit: opts.limit ?? 30,
        },
        { skipLoading: true } as any,
      )
      if (res?.success) {
        const data = (res.data ?? {}) as ListResponseData
        items.value = data.items ?? []
        unreadCount.value = Number(data.unread_count ?? 0)
        lastId.value = items.value.length > 0 ? Number(items.value[0].id) : 0
        initialLoaded.value = true
      }
    } catch (e: any) {
      error.value = e?.message ?? 'Failed to load notifications.'
    } finally {
      loading.value = false
    }
  }

  /**
   * Polling tick — asks the server for anything strictly newer than
   * `lastId`. Newly-arrived unread rows are prepended onto `items` AND
   * pushed onto `bubbleQueue` so the bottom-right bubble container surface
   * shows a sticky heads-up the user has to click through.
   */
  async function poll() {
    try {
      const res = await api.post<PollResponseData>(
        BASE + 'poll',
        { since_id: lastId.value, limit: 20 },
        { skipLoading: true, hideError: true } as any,
      )
      if (!res?.success) return
      const data = (res.data ?? {}) as PollResponseData
      const fresh = (data.items ?? []).filter((n) => !items.value.some((x) => x.id === n.id))

      if (fresh.length > 0) {
        items.value = [...fresh, ...items.value]
        lastId.value = Math.max(lastId.value, ...fresh.map((n) => Number(n.id)))

        for (const n of fresh) {
          if (n.read_at) continue
          // Sticky bubble — no auto-dismiss timer. The container surface
          // (NotificationBubbleContainer.vue) renders the queue bottom-right
          // and only removes a bubble when the user clicks it open or
          // dismisses it via the X button. Dedupe on id in case poll
          // overlaps re-deliver the same row.
          if (!bubbleQueue.value.some((b) => b.id === n.id)) {
            bubbleQueue.value.unshift(n)
          }
        }
      }
      unreadCount.value = Number(data.unread_count ?? 0)
    } catch {
      // Polling is best-effort; never bubble an error to the user.
    }
  }

  function startPolling(intervalMs = 30_000) {
    stopPolling()
    pollTimer = setInterval(poll, intervalMs)
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  async function markRead(ids: number | number[]) {
    const list = Array.isArray(ids) ? ids : [ids]
    if (list.length === 0) return
    try {
      const res = await api.post<{ updated: number }>(
        BASE + 'mark-read',
        { ids: list },
        { skipLoading: true } as any,
      )
      if (res?.success) {
        const now = new Date().toISOString()
        for (const n of items.value) {
          if (list.includes(n.id) && !n.read_at) n.read_at = now
        }
        unreadCount.value = Math.max(0, unreadCount.value - Number(res.data?.updated ?? 0))
      }
    } catch {
      /* tolerated */
    }
  }

  async function markAllRead() {
    try {
      const res = await api.post<{ updated: number }>(
        BASE + 'mark-all-read',
        {},
        { skipLoading: true } as any,
      )
      if (res?.success) {
        const now = new Date().toISOString()
        for (const n of items.value) {
          if (!n.read_at) n.read_at = now
        }
        unreadCount.value = 0
      }
    } catch {
      /* tolerated */
    }
  }

  async function dismiss(id: number) {
    try {
      const res = await api.post<{ updated: number }>(
        BASE + 'dismiss',
        { id },
        { skipLoading: true } as any,
      )
      if (res?.success) {
        items.value = items.value.filter((n) => n.id !== id)
        // Unread count is server-authoritative — refresh on next poll.
      }
    } catch {
      /* tolerated */
    }
  }

  async function dismissAll() {
    try {
      const res = await api.post<{ updated: number }>(
        BASE + 'dismiss-all',
        {},
        { skipLoading: true } as any,
      )
      if (res?.success) {
        items.value = []
        unreadCount.value = 0
      }
    } catch {
      /* tolerated */
    }
  }

  function reset() {
    stopPolling()
    items.value = []
    unreadCount.value = 0
    lastId.value = 0
    initialLoaded.value = false
    error.value = null
    pendingSalesOrderUid.value = null
    bubbleQueue.value = []
  }

  /** Remove a bubble from the bottom-right stack. Called both when the
   *  cashier explicitly hits its X button AND after they click the bubble
   *  open (the navigation handler dismisses on its way out). Idempotent —
   *  a bubble already gone is a no-op. */
  function dismissBubble(id: number) {
    bubbleQueue.value = bubbleQueue.value.filter((n) => n.id !== id)
  }

  /** Drop every pending bubble. Useful on logout / explicit "mark all read"
   *  so the cashier isn't greeted by a stale stack on the next session. */
  function clearBubbles() {
    bubbleQueue.value = []
  }

  /**
   * Push a CLIENT-SIDE synthetic bubble onto the bottom-right stack without
   * a server-issued row backing it. Used by polling surfaces inside the SPA
   * (e.g. the POS deliveries tab) that detect a new arrival locally and
   * want the same heads-up affordance the server-driven inbox provides.
   *
   * Caller passes whatever they have (title required; body/severity/link/
   * category optional). The id is synthesised as a NEGATIVE number derived
   * from `performance.now()` so it can never collide with a positive
   * server-assigned id — that keeps {@see dismissBubble} predicate-safe and
   * makes these locals easy to ignore in the server-truth scopes
   * (`items`/`unreadCount` which only ever count real inbox rows).
   *
   * Dedupes on `meta.dedupe_key` if supplied so a polling loop that
   * re-detects the same surfaced row doesn't stack identical bubbles.
   */
  function pushBubble(
    init: Partial<PosNotification> & { title: string; meta?: Record<string, any> },
  ): PosNotification {
    const dedupeKey = init.meta?.dedupe_key
    if (dedupeKey) {
      const dup = bubbleQueue.value.find((b) => b.meta?.dedupe_key === dedupeKey)
      if (dup) return dup
    }

    const synthesised: PosNotification = {
      id: -Math.floor((typeof performance !== 'undefined' ? performance.now() : Date.now()) * 1000)
        - Math.floor(Math.random() * 1000),
      uid: init.uid ?? '',
      record_type: init.record_type ?? null,
      record_id: init.record_id ?? null,
      title: init.title,
      body: init.body ?? null,
      category: init.category ?? null,
      severity: init.severity ?? 'info',
      link: init.link ?? null,
      meta: init.meta ?? {},
      from_users_id: init.from_users_id ?? null,
      read_at: null,
      dismissed_at: null,
      created_at: init.created_at ?? new Date().toISOString(),
    }
    bubbleQueue.value.unshift(synthesised)
    return synthesised
  }

  /** Signal the POS Screen to mount the given sales-order modal. The drawer
   *  calls this in lieu of router.push() when a notification's `link` field
   *  is a `sales-order:UID` deep-link, so the buyer-side payment confirmation
   *  surface lands on the cashier's screen without a full route change. */
  function openSalesOrder(uid: string) {
    if (!uid) return
    pendingSalesOrderUid.value = uid
  }

  /** Watcher in POSListView calls this once the modal mounts so a follow-up
   *  notification for a different order doesn't get masked by a stale ref. */
  function consumePendingSalesOrder() {
    pendingSalesOrderUid.value = null
  }

  return {
    items,
    unreadCount,
    lastId,
    initialLoaded,
    loading,
    error,
    unread,
    recent,
    pendingSalesOrderUid,
    bubbleQueue,
    load,
    poll,
    startPolling,
    stopPolling,
    markRead,
    markAllRead,
    dismiss,
    dismissAll,
    reset,
    openSalesOrder,
    consumePendingSalesOrder,
    dismissBubble,
    clearBubbles,
    pushBubble,
  }
})


if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useNotificationsStore, import.meta.hot))
}
